import * as React from "react"
import { WorkItemErrorType } from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgInfoCircle } from "@digits-shared/components/SVGIcons/line/InfoCircle.svg"
import { SvgLinkExternal01 } from "@digits-shared/components/SVGIcons/line/LinkExternal01.svg"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import borders from "@digits-shared/themes/borders"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import { useUpdateParty } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/Actions/Shared"
import { useWorkItemPushFirstError } from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/hooks/useWorkItemPush"
import {
  DetailColumn,
  type WorkItemRowProps,
} from "src/frontend/components/OS/Applications/Boost/WorkItems/WorkItemRow/Shared/WorkItemRowSharedElements"
import {
  CATEGORY_WORK_ITEM_TYPES,
  useSupportingTransactions,
  VENDOR_WORK_ITEM_TYPES,
} from "src/frontend/components/OS/Shared/WorkItems/useSupportingTransactions"

export const quickbooksTransactionLink = (appName: string, externalId: string) =>
  `https://app.qbo.intuit.com/app/${appName}?txnId=${externalId}`

/*
  STYLES
*/

const ErrorTitle = styled.div`
  color: rgba(255, 162, 75, 0.9);
  font-size: 15px;
  margin-bottom: 10px;
  font-weight: ${fonts.weight.heavy};
  margin-right: 8px;
  display: flex;
  align-items: center;
`

const ErrorText = styled.div`
  font-size: 15px;
  margin-bottom: 10px;
  font-weight: ${fonts.weight.book};
`

const ErrorAction = styled.div`
  font-size: 15px;
  font-weight: ${fonts.weight.book};
`

const ExternalLink = styled.a`
  color: ${colors.primary};
  text-decoration: underline;
  margin-right: 4px;

  &:hover {
    text-shadow: ${borders.theme.dark.textShadow};
    cursor: pointer;
  }
`

const OpenLinkIcon = styled(SvgLinkExternal01)`
  ${svgPathStyles(colors.primary, 2)};
  height: 16px;
  width: 16px;
  margin-left: 4px;
`

const TooltipContainer = styled.div`
  display: inline-flex;
  position: relative;
  z-index: 1;
`

const InfoIcon = styled(SvgInfoCircle)`
  ${svgPathStyles(colors.white, 1.5)};
  width: 14px;
  height: 14px;
  margin-top: 2px;
`

const Bold = styled.span`
  font-weight: ${fonts.weight.medium};
`

/*
  COMPONENT
*/

export const WorkItemError: React.FC<WorkItemRowProps> = ({ workItem }) => {
  const errorType = useWorkItemPushFirstError(workItem)
  if (!errorType) {
    return null
  }

  switch (errorType) {
    case WorkItemErrorType.NotSupported:
      return <WorkItemNotSupportedError workItem={workItem} />

    case WorkItemErrorType.PeriodClosed:
      return <WorkItemPeriodClosedError workItem={workItem} />

    case WorkItemErrorType.DuplicateVendor:
      return <WorkItemDuplicateVendorError workItem={workItem} />

    case WorkItemErrorType.Other:
      return <WorkItemOtherError workItem={workItem} />

    case WorkItemErrorType.NoError:
      return null

    default:
      return <WorkItemDefaultError workItem={workItem} />
  }
}

const WorkItemNotSupportedError: React.FC<WorkItemRowProps> = ({ workItem }) => (
  <DetailColumn>
    <ErrorTitle>QBO Push Error</ErrorTitle>
    <ErrorText>Updating category for this transaction type is not supported.</ErrorText>
    <ErrorAction>
      Category update can be <QBOWorkItemLink workItem={workItem} linkText="made in QBO" />
    </ErrorAction>
  </DetailColumn>
)

const WorkItemPeriodClosedError: React.FC<WorkItemRowProps> = ({ workItem }) => (
  <DetailColumn>
    <ErrorTitle>QBO Push Error</ErrorTitle>
    <ErrorText>The accounting period for this transaction is closed.</ErrorText>
    <ErrorAction>
      Category update can be made once the
      <br /> period <QBOWorkItemLink workItem={workItem} linkText="is reopened in QBO" />
    </ErrorAction>
  </DetailColumn>
)

const WorkItemDuplicateVendorError: React.FC<WorkItemRowProps> = ({ workItem }) => {
  const updateParty = useUpdateParty(workItem)

  const errorText = React.useMemo(() => {
    if (updateParty?.name) {
      return (
        <>
          A vendor named <Bold>{updateParty.name}</Bold> already exists in QBO. Instead of renaming
          this vendor, assign <Bold>{updateParty.name}</Bold> to this transaction only.
        </>
      )
    }

    return "This vendor already exists in QBO. Instead of renaming this vendor, assign the vendor name to this transaction only."
  }, [updateParty])

  return (
    <DetailColumn>
      <ErrorTitle>QBO Push Error</ErrorTitle>
      <ErrorText style={{ fontSize: "13px" }}>{errorText}</ErrorText>
      {updateParty?.name && (
        <ErrorAction style={{ fontSize: "13px" }}>
          <QBOVendorsLink />
        </ErrorAction>
      )}
    </DetailColumn>
  )
}

const WorkItemOtherError: React.FC<WorkItemRowProps> = ({ workItem }) => {
  const errorText = React.useMemo(() => {
    if (CATEGORY_WORK_ITEM_TYPES.includes(workItem.itemType))
      return "Updating the category for this transaction is not supported."
    if (VENDOR_WORK_ITEM_TYPES.includes(workItem.itemType))
      return "Updating the vendor for this transaction is not supported."
    return "Updating this transaction is not supported."
  }, [workItem.itemType])

  return (
    <DetailColumn>
      <ErrorTitle>QBO Push Error</ErrorTitle>
      <ErrorText>{errorText}</ErrorText>
      <ErrorAction>
        <ExternalLink href="mailto:support@digits.com" rel="noreferrer">
          Contact Support
        </ExternalLink>
        for more details.
      </ErrorAction>
    </DetailColumn>
  )
}

const WorkItemDefaultError: React.FC<WorkItemRowProps> = ({ workItem }) => (
  <DetailColumn>
    <ErrorTitle>QBO Push Error</ErrorTitle>
    <ErrorText>Digits was unable to update this transaction in QBO.</ErrorText>
    <ErrorAction>
      <ExternalLink href="mailto:support@digits.com" rel="noreferrer">
        Contact Support
      </ExternalLink>
      for more details.
    </ErrorAction>
  </DetailColumn>
)

const QBOVendorsLink = React.forwardRef<HTMLDivElement>((_, ref) => (
  <TooltipContainer ref={ref}>
    <ExternalLink
      href="https://app.qbo.intuit.com/app/vendors"
      target="_blank"
      rel="noopener noreferrer"
    >
      View your vendors in QBO
      <OpenLinkIcon />
    </ExternalLink>
    <QBOLoginTooltip />
  </TooltipContainer>
))

const QBOWorkItemLink = React.forwardRef<HTMLDivElement, WorkItemRowProps & { linkText: string }>(
  ({ workItem, linkText }, ref) => {
    const { object: transaction } = useSupportingTransactions(workItem)
    const quickbooksAppName = transaction?.quickbooksAppName
    const externalId = transaction?.externalTransactionId

    if (!quickbooksAppName || !externalId) return <>{linkText}</>

    return (
      <TooltipContainer ref={ref}>
        <ExternalLink
          href={quickbooksTransactionLink(quickbooksAppName, externalId)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {linkText}
          <OpenLinkIcon />
        </ExternalLink>
        <QBOLoginTooltip />
      </TooltipContainer>
    )
  }
)

const QBOLoginTooltip: React.FC = () => (
  <DigitsTooltip content="Ensure you are logged into the QBO account for this client">
    <InfoIcon />
  </DigitsTooltip>
)
